/**
 * 組織レポート v3 の GB組織タイプ判定（仕様書 §04）。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB組織レポートの生成ロジック.html
 *
 * 【現行との関係】
 *  現行には別系統が2つある（orgTypeClassifier.ts の10タイプ、orgArchetypes.ts の12分類）が、
 *  どちらも判定の考え方が違うため使わない。現行はそのまま温存する。
 *
 * 【GB人財と別のルールにする理由（仕様書§04）】
 *  組織の4軸平均は打ち消し合って6付近に収まるため、GB人財と同じ「平均7点以上」では
 *  コードが作れない（丸山製作所は A6.8 B5.9 C6.7 D4.6 で7点以上が1つも無い）。
 *  そこで「社員のうち何割がその軸で優勢か」＝優勢率を使う。人数の話なので打ち消しが起きない。
 */
import type { AxisKey } from './gb74Report';
import { getOrgNullLevels, type OrgNullLevels } from './orgNullTable';
import {
  classifyOrgShapeV3,
  orgCodeFromShape,
  SHAPE_MIN_MEMBERS,
  type OrgShapeResultV3,
  type OrgShapeV3,
} from './orgShapeV3';

/** 個人がその軸で「優勢」とみなされる点数（0〜12スケール）。 */
export const AXIS_DOMINANT = 7;

/** 表示順・同点時の優先順。 */
export const AXIS_ORDER: AxisKey[] = ['A', 'B', 'C', 'D'];

/** 対角ペア（ダイヤモンドで向かい合う組）。分極率はこの2組で測る。 */
export const DIAGONAL_PAIRS: Array<[AxisKey, AxisKey]> = [
  ['A', 'C'],
  ['B', 'D'],
];

/**
 * 組織の型。象限分布による統一判定（orgShapeV3）の結果をそのまま使う。
 * 以前は個人タイプコードの最多占有率で判定していたが、社員の分布(1-3)と
 * 土台が違うため矛盾が起き続けた。2026-08-22 に一本化した。
 */
export type OrgShape = OrgShapeV3;

/** 型ごとの1行説明（型の3択カード用）。orgShapeCopyV3 に集約している。 */
export { SHAPE_NOTE as ORG_SHAPE_NOTE } from './orgShapeCopyV3';

/**
 * 集中型・分散型の閾値（仕様書§04の判定表）。
 *
 * 【注意】仕様書は「固定閾値ではなく帰無分布を使う。人数ごとに基準を持つ」と述べているが、
 * 判定表では②③だけ固定値になっている。ここは仕様書の判定表どおりに実装し、
 * 偶然の水準を下回る場合は isChanceLevel で示す（下の judge を参照）。
 */
export const CONCENTRATED_MIN = 0.4;
export const DISPERSED_MAX = 0.25;

/** これ未満の人数では型を判定しない（DB14 14-型-判定不可）。 */
export const MIN_MEMBERS_FOR_SHAPE = SHAPE_MIN_MEMBERS;

/** 1人分の4軸スコア（0〜12、合計24）。 */
export type AxisScores = Record<AxisKey, number>;

/** その人の優勢軸を点数の高い順に並べる（同点は A→B→C→D）。 */
export function dominantAxesOf(axis: AxisScores): AxisKey[] {
  return AXIS_ORDER.filter((k) => axis[k] >= AXIS_DOMINANT).sort(
    (a, b) => axis[b] - axis[a] || AXIS_ORDER.indexOf(a) - AXIS_ORDER.indexOf(b)
  );
}

/**
 * 個人のタイプコード（例: 'A' / 'AB' / '全方'）。
 *
 * 【並び順】点数の高い順にする。仕様書§04の例が「CB」と書いていること、および
 * 帰無分布の検算（22名で最多タイプ占有率の中央値18%）がこの並びでのみ一致することから、
 * AB と BA は別のコードとして数えるのが正。
 */
export function personTypeCode(axis: AxisScores): string {
  const d = dominantAxesOf(axis);
  return d.length > 0 ? d.join('') : '全方';
}

/** 手順1: 各軸の優勢率（7点以上の社員の割合）。 */
export function dominanceRates(members: AxisScores[]): Record<AxisKey, number> {
  const n = Math.max(1, members.length);
  const out = {} as Record<AxisKey, number>;
  AXIS_ORDER.forEach((k) => {
    out[k] = members.filter((m) => m[k] >= AXIS_DOMINANT).length / n;
  });
  return out;
}

/**
 * 手順2: 組織のタイプコード。
 * 4軸の平均優勢率を上回る軸を、率の高い順に並べる。
 *
 * 全軸が同率のときは「上回る軸」が無くなるため、その場合だけ '全方' を返す。
 */
/** 優勢率が平均を上回る軸を、率の高い順に並べる（3軸判定に使う）。 */
export function axesAboveAverageRate(rates: Record<AxisKey, number>): AxisKey[] {
  const avg = AXIS_ORDER.reduce((s, k) => s + rates[k], 0) / AXIS_ORDER.length;
  return AXIS_ORDER.filter((k) => rates[k] > avg).sort(
    (a, b) => rates[b] - rates[a] || AXIS_ORDER.indexOf(a) - AXIS_ORDER.indexOf(b)
  );
}

/**
 * 仕様書§04 手順2どおりの、優勢率から作るコード。
 * 表示には使わない（表示は orgCodeFromShape。型と土台を揃えるため）。検算・比較用に残す。
 */
export function orgTypeCode(rates: Record<AxisKey, number>): string {
  const avg = AXIS_ORDER.reduce((s, k) => s + rates[k], 0) / AXIS_ORDER.length;
  const picked = AXIS_ORDER.filter((k) => rates[k] > avg).sort(
    (a, b) => rates[b] - rates[a] || AXIS_ORDER.indexOf(a) - AXIS_ORDER.indexOf(b)
  );
  return picked.length > 0 ? picked.join('') : '全方';
}

/**
 * 手順3-a: 分極率。対角ペアで「両側の少ないほう × 2」の割合。A-C と B-D の大きいほう。
 *
 * 【仕様書§04から変えている点（2026-08-22）】
 *  仕様書は「対角ペアで片側だけが優勢な人の割合」＝両側の合計としているが、
 *  この式では全員が同じ側に寄っている場合も最大値になる。実際、全員がC優勢の
 *  組織（純粋な集中）で分極率が100%になり、二極型と判定されていた。
 *  二極は「両側にいる」ことなので、少ないほうの側で決まらなければならない。
 *  DB12（12-定義）も 2 × min(...) を採っており、そちらに合わせた。
 *
 *  仕様書の例（A側だけ優勢11名・C側だけ優勢9名の22名）は
 *    合計方式 20/22＝91% ／ min方式 2×9/22＝82%
 *  で、どちらも偶然基準を超えるため「二極型」という結論は変わらない。
 *  偶然基準は min 方式で作り直した（n=22 で 95%点 73%）。
 */
export function polarizationRate(members: AxisScores[]): number {
  const n = Math.max(1, members.length);
  const rates = DIAGONAL_PAIRS.map(([x, y]) => {
    const onlyX = members.filter((m) => m[x] >= AXIS_DOMINANT && m[y] < AXIS_DOMINANT).length;
    const onlyY = members.filter((m) => m[y] >= AXIS_DOMINANT && m[x] < AXIS_DOMINANT).length;
    return (2 * Math.min(onlyX, onlyY)) / n;
  });
  return Math.max(...rates);
}

/**
 * 仕様書§04の書き方どおりの分極率（両側の合計）。検算・比較用に残す。判定には使わない。
 */
export function polarizationRateSpec(members: AxisScores[]): number {
  const n = Math.max(1, members.length);
  const rates = DIAGONAL_PAIRS.map(
    ([x, y]) => members.filter((m) => m[x] >= AXIS_DOMINANT !== m[y] >= AXIS_DOMINANT).length / n
  );
  return Math.max(...rates);
}

/** 手順3-b: 最多タイプ占有率と、その内訳。 */
export function typeCodeCounts(members: AxisScores[]): Array<{ code: string; count: number }> {
  const map = new Map<string, number>();
  members.forEach((m) => {
    const c = personTypeCode(m);
    map.set(c, (map.get(c) ?? 0) + 1);
  });
  return [...map.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

/**
 * 最多タイプ占有率。「全方」（優勢軸が無い人）は数に入れない。
 *
 * 【全方を除く理由（2026-08-22）】
 *  集中型の文言は「似た持ち味の方が集まっています」。全方は持ち味が突出して
 *  いない人なので、全方が最多でも「似た持ち味が集まっている」とは言えない。
 *  実際、全方が45%を占める組織が集中型と判定され、同じレポートの社員の分布に
 *  「持ち味が四方に散らばっている」と出て矛盾していた。
 */
export function topTypeShare(members: AxisScores[]): number {
  const counts = typeCodeCounts(members).filter((c) => c.code !== '全方');
  return counts.length === 0 ? 0 : counts[0].count / Math.max(1, members.length);
}

export interface OrgTypeResultV3 {
  /** 組織のタイプコード（例: 'AC'） */
  code: string;
  /** 型。人数が足りないときは null */
  shape: OrgShape | null;
  /** 形の判定の詳細（どの対角・どの隣接ペア・どの象限か） */
  shapeDetail: OrgShapeResultV3;
  /** 各軸の優勢率 */
  rates: Record<AxisKey, number>;
  /** 4軸の平均優勢率（コード判定の基準線） */
  averageRate: number;
  /** 分極率 */
  polarRate: number;
  /** 最多タイプ占有率 */
  topShare: number;
  /** 個人タイプの内訳（多い順） */
  breakdown: Array<{ code: string; count: number }>;
  /** その人数における偶然の水準 */
  nullLevels: OrgNullLevels;
  /**
   * 判定が偶然の水準と区別できないか。
   * 集中型・分散型は固定閾値で判定するため、人数が少ないと偶然でも閾値を超える。
   * 例: 10名なら最多タイプ占有率40%は偶然でも5%の確率で起きる（帰無95%点＝40%）。
   * true のときは断定せず「参考」として出す想定。
   */
  isChanceLevel: boolean;
  memberCount: number;
}

/**
 * 仕様書§04の判定順にそって型を決める。
 *   ① 分極率 ≥ 偶然基準(帰無95%点)      → 二極型
 *   ② 最多タイプ占有率 ≥ 40%            → 集中型
 *   ③ 最多タイプ占有率 ≤ 25%            → 分散型
 *   ④ その間                            → 中間型
 */
export function classifyOrgTypeV3(members: AxisScores[]): OrgTypeResultV3 {
  const n = members.length;
  const rates = dominanceRates(members);
  const averageRate = AXIS_ORDER.reduce((s, k) => s + rates[k], 0) / AXIS_ORDER.length;
  const polarRate = polarizationRate(members);
  const breakdown = typeCodeCounts(members);
  // 「全方」は持ち味が突出していない人なので、最多タイプには数えない（topTypeShare を参照）
  const topShare = topTypeShare(members);
  const nullLevels = getOrgNullLevels(n);

  // 形は象限分布から決める（型と社員の分布を同じ判定にするため）
  // 優勢率が3軸ぶん高いかを先に判定する（象限の集中として語らないための例外）。
  const dominanceAxes = axesAboveAverageRate(rates);
  const thinAxis =
    dominanceAxes.length === 3
      ? (AXIS_ORDER.find((k) => !dominanceAxes.includes(k)) ?? null)
      : null;

  const shapeDetail = classifyOrgShapeV3(members, thinAxis);
  // コードも象限分布から出す（型と土台を1つにするため）
  const code = orgCodeFromShape(shapeDetail);
  const shape = shapeDetail.shape;

  // 形の判定が偶然の範囲と区別できるか（95%点に届いているか）
  const isChanceLevel = shapeDetail.isChanceLevel;

  return {
    code,
    shape,
    shapeDetail,
    rates,
    averageRate,
    polarRate,
    topShare,
    breakdown,
    nullLevels,
    isChanceLevel,
    memberCount: n,
  };
}
